import { useCallback, useState } from 'react'
import { FaClipboardList as RelatorioIcon } from 'react-icons/fa'
import { Button, Spinner } from 'react-bootstrap'

import { DateTime } from 'luxon'

import { Alert, Date as DateGateway } from '@core/gateways'
import { obterRelatorioConsumoLanche } from '@core/services/restaurante/relatorio-consumo-lanche'
import { exportarRelatorioConsumoLanche } from '@core/services/restaurante/exportar-relatorio-consumo-lanche'
import { RelatorioConsumoLancheItem } from '@core/types/services'
import { TablePage } from '@presentation/components/templates'
import { parseToRequest } from '@presentation/utils'
import { downloadBlobResponse } from '@presentation/utils/download'
import { FetchProps } from '@presentation/types/hooks'
import { PageProps } from '@presentation/types/components/templates'
import {
  Filter,
  FilterProps,
  TableColumn,
  TableProps
} from '@presentation/types/components/organisms'

import { ViewRelatorioConsumoLancheModal } from './components'
import { formatCpf } from './utils'

const INPUT_FORMAT = 'dd/MM/yyyy HH:mm'
const REQUEST_FORMAT = "yyyy-MM-dd'T'HH:mm"
const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 20
const DEFAULT_SORT_BY = 'data_horario_consumo'
const DEFAULT_SORT_DIR = 'desc'
const DEFAULT_EXPORT_FILE_NAME = 'relatorio-consumo-lanches.xlsx'

const formatDateTime = (value: string) => {
  if (!value) return ''

  return DateGateway.format({
    date: value,
    pattern: 'dd/LL/yyyy HH:mm'
  })
}

const toRequestDateTime = (value?: string) => {
  if (!value) return undefined

  const parsedValue = DateTime.fromFormat(value, INPUT_FORMAT)

  if (!parsedValue.isValid) {
    return value
  }

  return parsedValue.toFormat(REQUEST_FORMAT)
}

const cleanParams = (params: Record<string, any>) => {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      return (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      )
    })
  )
}

const mapParamsToRequest = (
  params: Record<string, any>,
  options?: {
    includePagination?: boolean
  }
) => {
  const {
    page,
    sortBy,
    sortDir,
    perPage,
    dataHoraInicio,
    dataHoraFim,
    ...rest
  } = params ?? {}
  const includePagination = options?.includePagination ?? true

  const mergedParams = cleanParams({
    ...(includePagination
      ? {
          page: page ?? DEFAULT_PAGE,
          per_page: perPage ?? DEFAULT_PER_PAGE
        }
      : {}),
    sort_by: sortBy ?? DEFAULT_SORT_BY,
    sort_dir: sortDir ?? DEFAULT_SORT_DIR,
    ...(dataHoraInicio ? { inicio: toRequestDateTime(dataHoraInicio) } : {}),
    ...(dataHoraFim ? { fim: toRequestDateTime(dataHoraFim) } : {}),
    ...rest
  })

  return parseToRequest(mergedParams)
}

const getResponseItems = (response: any): RelatorioConsumoLancheItem[] => {
  const data = response?.data ?? response
  const nestedData = data?.data ?? data
  const items = nestedData?.items ?? data?.items ?? []

  return Array.isArray(items) ? items : []
}

const getResponseTotal = (
  response: any,
  items: RelatorioConsumoLancheItem[]
) => {
  const data = response?.data ?? response
  const nestedData = data?.data ?? data

  return (
    nestedData?.pagination?.total ?? data?.pagination?.total ?? items.length
  )
}

const RelatorioConsumoLanchePage: React.FC = () => {
  const [data, setData] = useState<RelatorioConsumoLancheItem[]>([])
  const [isFetching, setIsFetching] = useState(true)
  const [totalItems, setTotalItems] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [currentParams, setCurrentParams] = useState<Record<string, any>>({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    sortBy: DEFAULT_SORT_BY,
    sortDir: DEFAULT_SORT_DIR
  })

  const fetchData = useCallback(async (input?: FetchProps) => {
    setIsFetching(true)

    const params = Object.assign(
      {},
      {
        page: DEFAULT_PAGE,
        perPage: DEFAULT_PER_PAGE,
        sortBy: DEFAULT_SORT_BY,
        sortDir: DEFAULT_SORT_DIR
      },
      input?.options,
      input?.queries
    )

    setCurrentParams(params)

    try {
      const response = await obterRelatorioConsumoLanche({
        params: mapParamsToRequest(params)
      })
      const items = getResponseItems(response)

      setData(items)
      setTotalItems(getResponseTotal(response, items))
    } catch (error) {
      await Alert.call({
        type: 'error',
        title: 'Falha ao Carregar Relatório',
        description:
          'Tente novamente mais tarde. Caso o problema persista, contate alguém do suporte técnico.'
      })
    } finally {
      setIsFetching(false)
    }
  }, [])

  const handleExport = useCallback(async () => {
    if (isExporting) return

    setIsExporting(true)

    try {
      const response = await exportarRelatorioConsumoLanche({
        params: mapParamsToRequest(currentParams, { includePagination: false })
      })

      downloadBlobResponse(response, DEFAULT_EXPORT_FILE_NAME)

      await Alert.callSuccess({
        title: 'Relatório exportado com sucesso.'
      })
    } catch (error) {
      await Alert.call({
        type: 'error',
        title: 'Falha ao Exportar Relatório',
        description:
          'Não foi possível baixar o arquivo agora. Tente novamente em instantes.'
      })
    } finally {
      setIsExporting(false)
    }
  }, [currentParams, isExporting])

  const pageConfigs: PageProps = {
    icon: <RelatorioIcon />,
    title: 'Relatório de Consumo de Lanches',
    description: 'Página de consulta do consumo de lanches dos alunos.',
    actions: [
      <Button variant="success" onClick={handleExport} disabled={isExporting}>
        {isExporting ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            Exportando...
          </>
        ) : (
          'Exportar .xlsx'
        )}
      </Button>
    ]
  }

  const filters: Filter[] = [
    {
      type: {
        value: 'nome',
        label: 'Nome'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'cpf',
        label: 'CPF'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'matricula',
        label: 'Matrícula'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'curso',
        label: 'Curso'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'turno_curso',
        label: 'Turno'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'tipo_lanche',
        label: 'Tipo de lanche'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'dataHoraInicio',
        label: 'Data/Hora início'
      },
      description: {
        type: 'input',
        configs: {
          type: 'datetime-local'
        }
      }
    },
    {
      type: {
        value: 'dataHoraFim',
        label: 'Data/Hora fim'
      },
      description: {
        type: 'input',
        configs: {
          type: 'datetime-local'
        }
      }
    }
  ]

  const filterConfigs: FilterProps = {
    filters,
    fetchData,
    hasStatus: false
  }

  const columns: Array<TableColumn<RelatorioConsumoLancheItem>> = [
    {
      name: 'Nome',
      selector: item => item.nome ?? '',
      center: true,
      grow: 2
    },
    {
      name: 'CPF',
      selector: item => formatCpf(item.cpf),
      center: true
    },
    {
      name: 'Matrícula',
      selector: item => item.matricula ?? '',
      center: true
    },
    {
      name: 'Curso',
      selector: item => item.curso ?? '',
      center: true,
      grow: 2
    },
    {
      name: 'Turno',
      selector: item => item.turno_curso ?? '',
      center: true
    },
    {
      name: 'Tipo de lanche',
      selector: item => item.tipo_lanche ?? '',
      center: true
    },
    {
      name: 'Data/Hora do consumo',
      selector: item => formatDateTime(item.data_horario_consumo),
      center: true,
      grow: 1.2
    },
    {
      name: 'Ações',
      cell: item => <ViewRelatorioConsumoLancheModal item={item} />,
      center: true,
      width: '90px'
    }
  ]

  const tableConfigs: TableProps = {
    columns,
    data,
    apiConfigs: {
      fetchData,
      isFetching,
      totalItems
    }
  }

  return (
    <TablePage
      pageConfigs={pageConfigs}
      filterConfigs={filterConfigs}
      tableConfigs={tableConfigs}
    />
  )
}

export default RelatorioConsumoLanchePage
